/**
 * Plane geometry for the microeconomics graphs: clipping a curve to the plotting area.
 *
 * Supply and demand lines are drawn from where they cross the axes, so the caller hands
 * over the whole line and the chart keeps the part inside the box that starts at the
 * origin, the way a textbook graph does.
 */

export type XY = readonly [number, number];

export interface Box {
  xMax: number;
  yMax: number;
}

/**
 * The part of the segment a→b inside [0, xMax] × [0, yMax] (Liang–Barsky), or null when
 * none of it is.
 */
export function clipSegment(a: XY, b: XY, box: Box): [XY, XY] | null {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const edges: [number, number][] = [
    [-dx, a[0]],
    [dx, box.xMax - a[0]],
    [-dy, a[1]],
    [dy, box.yMax - a[1]],
  ];
  let t0 = 0;
  let t1 = 1;
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) {
        return null;
      }
    } else {
      const r = q / p;
      if (p < 0) {
        t0 = Math.max(t0, r);
      } else {
        t1 = Math.min(t1, r);
      }
    }
  }
  if (t0 > t1) {
    return null;
  }
  return [
    [a[0] + t0 * dx, a[1] + t0 * dy],
    [a[0] + t1 * dx, a[1] + t1 * dy],
  ];
}

/** A polyline clipped to the box: the pieces that remain, in order. */
export function clipPolyline(points: readonly XY[], box: Box): XY[][] {
  const pieces: XY[][] = [];
  let current: XY[] = [];
  for (let i = 1; i < points.length; i++) {
    const clipped = clipSegment(points[i - 1], points[i], box);
    if (!clipped) {
      if (current.length > 0) {
        pieces.push(current);
        current = [];
      }
      continue;
    }
    const [start, end] = clipped;
    const last = current.at(-1);
    if (last && last[0] === start[0] && last[1] === start[1]) {
      current.push(end);
    } else {
      if (current.length > 0) {
        pieces.push(current);
      }
      current = [start, end];
    }
  }
  if (current.length > 0) {
    pieces.push(current);
  }
  return pieces;
}

/**
 * The straight line through two points, ordered left to right, long enough to run past any box that starts at
 * the origin and holds both points. Vertical and horizontal lines are handled.
 */
export function lineThrough(a: XY, b: XY, reach: number): [XY, XY] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return [a, b];
  }
  const scale = reach / length;
  const back: XY = [a[0] - dx * scale, a[1] - dy * scale];
  const ahead: XY = [a[0] + dx * scale, a[1] + dy * scale];
  // Left to right (bottom to top for a vertical line), so a label at the end of the
  // line sits where a textbook puts it.
  const reversed =
    ahead[0] < back[0] || (ahead[0] === back[0] && ahead[1] < back[1]);
  return reversed ? [ahead, back] : [back, ahead];
}

function insideEdge(point: XY, edge: number, box: Box): boolean {
  switch (edge) {
    case 0:
      return point[0] >= 0;
    case 1:
      return point[0] <= box.xMax;
    case 2:
      return point[1] >= 0;
    default:
      return point[1] <= box.yMax;
  }
}

/** Where the segment a→b meets the box edge `edge` (0 left, 1 right, 2 bottom, 3 top). */
function crossEdge(a: XY, b: XY, edge: number, box: Box): XY {
  if (edge < 2) {
    const x = edge === 0 ? 0 : box.xMax;
    const t = (x - a[0]) / (b[0] - a[0]);
    return [x, a[1] + t * (b[1] - a[1])];
  }
  const y = edge === 2 ? 0 : box.yMax;
  const t = (y - a[1]) / (b[1] - a[1]);
  return [a[0] + t * (b[0] - a[0]), y];
}

/**
 * A polygon clipped to the box (Sutherland–Hodgman). Shaded surplus triangles can dip
 * below the horizontal axis when a supply line starts at a negative price; only the part
 * a textbook graph shows is kept.
 */
export function clipPolygon(points: readonly XY[], box: Box): XY[] {
  let polygon: XY[] = [...points];
  for (let edge = 0; edge < 4 && polygon.length > 0; edge++) {
    const input = polygon;
    polygon = [];
    for (const [index, current] of input.entries()) {
      const previous = input[(index + input.length - 1) % input.length];
      const currentIn = insideEdge(current, edge, box);
      const previousIn = insideEdge(previous, edge, box);
      if (currentIn !== previousIn) {
        polygon.push(crossEdge(previous, current, edge, box));
      }
      if (currentIn) {
        polygon.push(current);
      }
    }
  }
  return polygon;
}
